/**
 * Locale-aware reader for Sanity localeString / localeText /
 * localePortableText fields.
 *
 * Sanity stores localized fields as plain objects keyed by locale id:
 *
 *   { en: 'Aftercare', es: 'Cuidado posterior', ja: 'アフターケア', … }
 *
 * `pickLocale(value, locale)` returns the value for `locale` if
 * present and non-empty, otherwise falls through to English, then to
 * the first non-empty value in the object, then to the optional
 * `fallback` string.
 *
 * Mirrors the mobile-side helper in
 * `services/supabase/api/content/_shared.ts` (kept as a separate copy
 * because the web is server-rendered and pulls Sanity data directly,
 * whereas mobile reads through a Supabase facade). Keep the two
 * implementations in lockstep when changing fallback rules.
 *
 * Usage in a server component:
 *
 *   import { getLocale } from 'next-intl/server'
 *   import { pickLocale, pickPortableText } from '@/lib/i18n/pickLocale'
 *
 *   const locale = await getLocale()
 *   const title = pickLocale(post.title, locale, 'Untitled')
 *   const body = pickPortableText(post.body, locale)
 *
 * In a client component, swap `getLocale()` for `useLocale()` from
 * 'next-intl'.
 */

const DEFAULT_LOCALE = 'en'

/**
 * Localized field shape — an object whose keys are locale ids and
 * values are strings (for localeString / localeText). Tolerates `null`
 * and `undefined` in the value slot since Sanity sometimes serializes
 * empty translations as `null`.
 */
export type LocaleStringMap = Record<string, string | null | undefined>

/**
 * Read a localeString / localeText value.
 *
 *   1. Active locale's value, if a non-empty string.
 *   2. English fallback, if a non-empty string.
 *   3. First non-empty value in the object.
 *   4. The optional `fallback` arg.
 *   5. Empty string.
 *
 * Returns a string (never null/undefined) so JSX can render it
 * unconditionally.
 */
export function pickLocale(
  value: LocaleStringMap | string | null | undefined,
  locale: string,
  fallback = '',
): string {
  if (value == null) return fallback
  if (typeof value === 'string') return value.trim() ? value : fallback

  // Active locale.
  const active = value[locale]
  if (typeof active === 'string' && active.trim().length > 0) return active

  // English (whether or not it's the active locale; covers the common
  // case where a brand-new translator hasn't filled this field yet).
  if (locale !== DEFAULT_LOCALE) {
    const en = value[DEFAULT_LOCALE]
    if (typeof en === 'string' && en.trim().length > 0) return en
  }

  // Last resort: any non-empty value in the object so the page never
  // renders "Untitled" when *some* translation does exist. Skip any
  // key starting with `_` — those are Sanity's meta fields (`_type`,
  // `_key`, `_ref`, …) which would otherwise leak through. Without
  // this guard a localePortableText value passed to pickLocale by
  // mistake would surface its `_type` ("localePortableText") as the
  // visible text.
  //
  // IMPORTANT: only run this when the active locale is NOT the
  // default. For an English (default-locale) viewer, an empty `en`
  // means the field is empty — period. Falling back to whatever
  // other-language string exists in the object would surface (e.g.)
  // Arabic excerpt text on the English blog index when an editor
  // cleared `en` after a TranslatePress import had already populated
  // the per-locale rows. The non-default branch keeps the
  // "translators viewing partially-translated content shouldn't see
  // the field collapse to nothing" behaviour intact.
  if (locale !== DEFAULT_LOCALE) {
    for (const key of Object.keys(value)) {
      if (key.startsWith('_')) continue
      const v = value[key]
      if (typeof v === 'string' && v.trim().length > 0) return v
    }
  }

  return fallback
}

/**
 * Like `pickLocale`, but ALSO reports which locale the string came from when
 * that differs from the active `locale` — the localeString counterpart of
 * `pickPortableTextWithLang`, for WCAG 3.1.2 `lang` marking of a fallback.
 * `lang` is null when the value came from the active locale, was a bare
 * string, or is the `fallback` arg. `value` is always identical to
 * `pickLocale(value, locale, fallback)`.
 */
export function pickLocaleWithLang(
  value: LocaleStringMap | string | null | undefined,
  locale: string,
  fallback = '',
): { value: string; lang: string | null } {
  const picked = pickLocale(value, locale, fallback)
  if (value == null || typeof value === 'string') return { value: picked, lang: null }
  const active = value[locale]
  if (typeof active === 'string' && active.trim().length > 0) return { value: picked, lang: null }
  if (locale !== DEFAULT_LOCALE) {
    const en = value[DEFAULT_LOCALE]
    if (typeof en === 'string' && en.trim().length > 0) return { value: picked, lang: DEFAULT_LOCALE }
    for (const key of Object.keys(value)) {
      if (key.startsWith('_')) continue
      const v = value[key]
      if (typeof v === 'string' && v.trim().length > 0) return { value: picked, lang: key }
    }
  }
  return { value: picked, lang: null }
}

/**
 * Read an **English-only** SEO override (`seo.metaTitle` /
 * `seo.metaDescription`). Returns the English value only on the default
 * locale; for every other locale it returns '' so the caller falls back
 * to the already-translated `title` / `excerpt`.
 *
 * We intentionally do NOT use pickLocale's English fallback here — that
 * would leak the English meta onto every localized page. We don't
 * maintain translated meta: SEO is optimized for English, and other
 * locales reuse their translated title/excerpt for the page
 * `<title>` / `<meta description>`.
 */
export function pickSeoOverride(
  value: LocaleStringMap | string | null | undefined,
  locale: string,
): string {
  if (locale !== DEFAULT_LOCALE) return ''
  if (value == null) return ''
  const raw = typeof value === 'string' ? value : value[DEFAULT_LOCALE]
  if (typeof raw !== 'string' || raw.trim().length === 0) return ''
  // Guard against un-resolved SEO template tokens that slipped through the WP
  // import — Rank Math / Yoast variables like "%excerpt%", "%title%",
  // "%%sitename%%". Its default meta-description template is the bare token
  // "%excerpt%", which was imported verbatim onto some docs and rendered the
  // literal "%excerpt%" into link previews. If the value is ONLY such tokens
  // (plus separators), it isn't real copy — ignore it so the caller falls back
  // to the translated title / excerpt. Real copy that merely contains a "%"
  // (e.g. "Save 20% today") is preserved: a token needs a "%" on BOTH sides.
  if (/^[\s|·,–—-]*(?:%+[a-z0-9_]+%+[\s|·,–—-]*)+$/i.test(raw)) return ''
  return raw
}

/**
 * Localized portable-text shape — keyed by locale, value is an array
 * of Sanity blocks.
 */
export type LocalePortableTextMap = Record<string, unknown[] | null | undefined>

/**
 * Read a localePortableText value. Returns the active locale's block
 * array if non-empty, otherwise the English block array, otherwise
 * the first non-empty block array in the object, otherwise an empty
 * array. Same fallback ladder as `pickLocale`.
 *
 * Generic over the block type — caller can pass `PortableTextBlock`
 * (or any other) so the result types correctly without a cast at the
 * call site:
 *
 *   const body = pickPortableText<PortableTextBlock>(post.body, locale)
 *   <PortableTextRenderer value={body} />  // already PortableTextBlock[]
 *
 * Defaults to `unknown` for callers that don't supply a type — which
 * still works at the consumer with a cast, just less elegant.
 */
export function pickPortableText<T = unknown>(
  value:
    | Record<string, T[] | null | undefined>
    | T[]
    | null
    | undefined,
  locale: string,
): T[] {
  return pickPortableTextWithLang<T>(value, locale).value
}

/**
 * Like `pickPortableText`, but ALSO reports which locale key the
 * returned block array actually came from — so a caller can mark the
 * rendered prose with the correct `lang` for WCAG 3.1.2 ("Language of
 * Parts") when the content fell back to a language other than the
 * active one.
 *
 * Returns `{ value, lang }` where:
 *   • `value` is the chosen block array (identical to what
 *     `pickPortableText` returns — this is the single source of truth).
 *   • `lang` is the locale id the content came from, BUT ONLY when it
 *     differs from the active `locale` (a genuine fallback that needs
 *     marking). It is `null` when:
 *       – the content came from the active `locale` (no fallback — the
 *         page root's `lang=<locale>` already covers it, no marking);
 *       – `value` was a bare array (locale unknown, nothing to mark);
 *       – the result is empty.
 *
 * The fallback ladder is identical to `pickPortableText` /
 * `pickLocale`: active locale → English → first non-empty (only when
 * the active locale isn't English, matching the English-viewer guard).
 *
 *   const { value, lang } = pickPortableTextWithLang(doc.body, locale)
 *   <PortableTextRenderer value={value} lang={lang} />
 */
export function pickPortableTextWithLang<T = unknown>(
  value:
    | Record<string, T[] | null | undefined>
    | T[]
    | null
    | undefined,
  locale: string,
): { value: T[]; lang: string | null } {
  if (!value) return { value: [], lang: null }
  // Bare array — locale is unknown, so there's nothing to mark.
  if (Array.isArray(value)) return { value, lang: null }

  const active = (value as Record<string, T[] | null | undefined>)[locale]
  // Content in the active locale: no fallback, so no `lang` marking
  // (the page root already carries `lang=<locale>`).
  if (Array.isArray(active) && active.length > 0) return { value: active, lang: null }

  if (locale !== DEFAULT_LOCALE) {
    const en = (value as Record<string, T[] | null | undefined>)[DEFAULT_LOCALE]
    if (Array.isArray(en) && en.length > 0) return { value: en, lang: DEFAULT_LOCALE }
  }

  // Same English-viewer guard as `pickLocale` — without it, an
  // English viewer with empty `body.en` would see (e.g.) the Arabic
  // body if any locale was populated. Only fall through to "any
  // non-empty translation" when the active locale isn't English.
  if (locale !== DEFAULT_LOCALE) {
    for (const key of Object.keys(value)) {
      const v = (value as Record<string, T[] | null | undefined>)[key]
      if (Array.isArray(v) && v.length > 0) return { value: v, lang: key }
    }
  }
  return { value: [], lang: null }
}
